export type AdminOperationMethod = "DELETE" | "GET" | "PATCH" | "POST" | "PUT";

export interface AdminOperationDescriptor {
  readonly id: string;
  readonly method: AdminOperationMethod;
  readonly path: string;
}

export type AdminOperationPathParams = Record<string, string | number>;
export type AdminOperationQuery = Record<string, unknown>;

export const ADMIN_OPERATIONS = {
  listAdminFeedback: operation("listAdminFeedback", "GET", "/v1/api/admin/feedback"),
  listAdminFeedbackReplies: operation(
    "listAdminFeedbackReplies",
    "GET",
    "/v1/api/admin/feedback/{storeHash}/{feedbackId}/replies",
  ),
  createAdminFeedbackReply: operation(
    "createAdminFeedbackReply",
    "POST",
    "/v1/api/admin/feedback/{storeHash}/{feedbackId}/replies",
  ),
  updateAdminFeedbackStatus: operation(
    "updateAdminFeedbackStatus",
    "PATCH",
    "/v1/api/admin/feedback/{storeHash}/{feedbackId}/status",
  ),

  manualFixtureSetting: operation(
    "manualFixtureSetting",
    "GET",
    "/v1/api/admin/manual-fixture-setting",
  ),
  manualFixtureSettingUpdate: operation(
    "manualFixtureSettingUpdate",
    "PUT",
    "/v1/api/admin/manual-fixture-setting",
  ),
  globalWidgetSetting: operation(
    "globalWidgetSetting",
    "GET",
    "/v1/api/admin/global-widget-setting",
  ),
  globalWidgetSettingUpdate: operation(
    "globalWidgetSettingUpdate",
    "PUT",
    "/v1/api/admin/global-widget-setting",
  ),
  globalCompetitors: operation("globalCompetitors", "GET", "/v1/api/admin/global-competitors"),
  globalCompetitorUpsert: operation(
    "globalCompetitorUpsert",
    "POST",
    "/v1/api/admin/global-competitors",
  ),
  globalCompetitorDelete: operation(
    "globalCompetitorDelete",
    "DELETE",
    "/v1/api/admin/global-competitors/{normalizedHost}",
  ),
  audit: operation("audit", "GET", "/v1/api/admin/audit"),
  authLogin: operation("authLogin", "GET", "/v1/api/admin/auth/login"),
  authLogout: operation("authLogout", "POST", "/v1/api/admin/auth/logout"),
  domainHealth: operation("domainHealth", "GET", "/v1/api/admin/extraction/health"),
  domainStatus: operation("domainStatus", "PATCH", "/v1/api/admin/extraction/domains/{domain}"),
  dataBrowserModels: operation("dataBrowserModels", "GET", "/v1/api/admin/data-browser/models"),
  dataBrowserQuery: operation("dataBrowserQuery", "GET", "/v1/api/admin/data-browser/query"),
  dataBrowserRecord: operation(
    "dataBrowserRecord",
    "GET",
    "/v1/api/admin/data-browser/records/{encodedRecordKey}",
  ),
  dataBrowserRecordDelete: operation(
    "dataBrowserRecordDelete",
    "DELETE",
    "/v1/api/admin/data-browser/records/{encodedRecordKey}",
  ),
  bedrockPromptDiagnostic: operation(
    "bedrockPromptDiagnostic",
    "POST",
    "/v1/worker/bedrock/prompt",
  ),
  extractionDiagnostic: operation("extractionDiagnostic", "POST", "/v1/worker/extraction/test"),
  installations: operation("installations", "GET", "/v1/api/admin/installations"),
  jobDetail: operation("jobDetail", "GET", "/v1/api/admin/jobs/{jobId}"),
  jobRetry: operation("jobRetry", "POST", "/v1/api/admin/jobs/{jobId}/retry"),
  jobs: operation("jobs", "GET", "/v1/api/admin/jobs"),
  operationsHealth: operation("operationsHealth", "GET", "/v1/api/admin/operations/health"),
  providerStatus: operation("providerStatus", "PATCH", "/v1/api/admin/extraction/providers/{id}"),
  session: operation("session", "GET", "/v1/api/admin/session"),
  storeAudit: operation("storeAudit", "GET", "/v1/api/admin/stores/{storeHash}/audit"),
  storeDetail: operation("storeDetail", "GET", "/v1/api/admin/stores/{storeHash}"),
  storeHealth: operation("storeHealth", "GET", "/v1/api/admin/stores/{storeHash}/health"),
  storeHistory: operation(
    "storeHistory",
    "GET",
    "/v1/api/admin/stores/{storeHash}/installation-history",
  ),
  storeWebhooks: operation("storeWebhooks", "GET", "/v1/api/admin/stores/{storeHash}/webhooks"),
  reconcileStoreWebhooks: operation(
    "reconcileStoreWebhooks",
    "POST",
    "/v1/api/admin/stores/{storeHash}/webhooks/reconcile",
  ),
  storeMerchantImpersonation: operation(
    "storeMerchantImpersonation",
    "POST",
    "/v1/api/admin/stores/{storeHash}/merchant-impersonation",
  ),
  storeNotesList: operation("storeNotesList", "GET", "/v1/api/admin/stores/{storeHash}/notes"),
  storeNotes: operation("storeNotes", "POST", "/v1/api/admin/stores/{storeHash}/notes"),
  storeNoteUpdate: operation(
    "storeNoteUpdate",
    "PATCH",
    "/v1/api/admin/stores/{storeHash}/notes/{noteId}",
  ),
  storeNoteDelete: operation(
    "storeNoteDelete",
    "DELETE",
    "/v1/api/admin/stores/{storeHash}/notes/{noteId}",
  ),
  storeResume: operation("storeResume", "POST", "/v1/api/admin/stores/{storeHash}/resume"),
  stores: operation("stores", "GET", "/v1/api/admin/stores"),
  storeSupportAccess: operation(
    "storeSupportAccess",
    "POST",
    "/v1/api/admin/stores/{storeHash}/support-access",
  ),
  storeSuspend: operation("storeSuspend", "POST", "/v1/api/admin/stores/{storeHash}/suspend"),
  usage: operation("usage", "GET", "/v1/api/admin/usage"),
} as const satisfies Record<string, AdminOperationDescriptor>;

export type AdminOperationId = keyof typeof ADMIN_OPERATIONS;

export function adminOperationPath(
  descriptor: AdminOperationDescriptor,
  pathParams: AdminOperationPathParams = {},
  query: AdminOperationQuery = {},
): string {
  const path = interpolatePathParams(descriptor.path, pathParams);
  const queryString = toQueryString(query);
  return queryString.length === 0 ? path : `${path}?${queryString}`;
}

function operation(
  id: string,
  method: AdminOperationMethod,
  path: string,
): AdminOperationDescriptor {
  return { id, method, path };
}

function interpolatePathParams(path: string, pathParams: AdminOperationPathParams): string {
  return path.replace(/\{([^}]+)\}/g, (_, key: string) => {
    const value = pathParams[key];
    if (value === undefined) {
      throw new Error(`Missing Admin API path parameter: ${key}`);
    }
    return encodeURIComponent(String(value));
  });
}

function toQueryString(query: AdminOperationQuery): string {
  return new URLSearchParams(
    Object.entries(query)
      .filter(([, value]) => value !== undefined && value !== null && value !== "")
      .map(([key, value]) => [key, String(value)]),
  ).toString();
}
